import { ArrowLeftIcon, PhotoIcon, UserCircleIcon, TicketIcon } from '@heroicons/react/20/solid'
import { useTranslation } from 'react-i18next'
import 'leaflet/dist/leaflet.css'
import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useLazyQuery } from '@apollo/client'
import { GET_BOAT_DAY_PASS_PRICE } from '../../../graphql/booking/queries'
import { useMutation } from '@apollo/client'
import { ADD_DAY_PASS_PRICE, UPDATE_DAY_PASS_PRICE } from '../../../graphql/booking/mutations'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import Flatpickr from 'react-flatpickr'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'

export default function DayPasForm() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { t } = useTranslation()

    const [passData, setPassData] = useState(null)
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [dialogError, setDialogError] = useState('')
    const [submitLoading, setSubmitLoading] = useState(false)

    const [getPassPriceList, { loading: passPriceListLoading, error: passPriceListError }] = useLazyQuery(
        GET_BOAT_DAY_PASS_PRICE,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.getBoatDayPassPrices.success && data.getBoatDayPassPrices.code === '200') {
                    setPassData(data.getBoatDayPassPrices.day_pass_prices[0])
                }
            },
        }
    )

    const searchBoatPass = () => {
        getPassPriceList({
            variables: {
                page: {
                    limit: 1,
                    offset: 0,
                },
                priceFilter: {
                    master_price_id: id,
                    valid_from: null,
                    valid_to: null,
                    status_code: null,
                    price_status_code: null,
                },
            },
        })
    }

    useEffect(() => {
        if (id) {
            searchBoatPass()
        } else {
            setPassData(null)
        }
    }, [id])

    const handleBack = () => {
        navigate('/day-pass/price')
    }

    const handleDialogClose = () => {
        setShowSucDialog(false)
        setShowErrDialog(false)
    }

    const [addNewBoatDayPass, { loading: addLoading }] = useMutation(ADD_DAY_PASS_PRICE, {
        onCompleted: data => {
            if (data?.addBoatDayPassPrice.code === '200' && data?.addBoatDayPassPrice.success === true) {
                CreateBoatDayPassFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setShowSucDialog(true)
                setTimeout(() => {
                    handleBack()
                }, 500)
            } else {
                CreateBoatDayPassFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setDialogError(data?.addBoatDayPassPrice.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            CreateBoatDayPassFormValidation.setSubmitting(false)
            setDialogError(error.message)
            setShowErrDialog(true)
        },
    })

    const [updateBoatDayPass, { loading: updateLoading }] = useMutation(UPDATE_DAY_PASS_PRICE, {
        onCompleted: data => {
            if (data?.updateBoatDayPassPrice.code === '200' && data?.updateBoatDayPassPrice.success === true) {
                CreateBoatDayPassFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setShowSucDialog(true)
                setTimeout(() => {
                    handleBack()
                }, 500)
            } else {
                CreateBoatDayPassFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setDialogError(data?.updateBoatDayPassPrice.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            CreateBoatDayPassFormValidation.setSubmitting(false)
            setDialogError(error.message)
            setShowErrDialog(true)
        },
    })

    const CreateBoatDayPassFormValidation = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: {
                en: passData?.name?.en || '',
                ar: passData?.name?.ar || '',
            },
            description: {
                en: passData?.description?.en || '',
                ar: passData?.description?.ar || '',
            },
            valid_from: passData?.valid_from || '',
            valid_to: passData?.valid_to || '',
            status_code: passData?.day_pass_price_status?.code || 'ACT',
            price: [
                {
                    price_id: passData?.price?.[0]?.price_id || null,
                    seat_type_code: passData?.price?.[0]?.seat_type_code || 'STD',
                    price: {
                        ADL: passData?.price?.[0]?.price?.ADL || '',
                        CHD: passData?.price?.[0]?.price?.CHD || '',
                        INF: passData?.price?.[0]?.price?.INF || '',
                        SNR: passData?.price?.[0]?.price?.SNR || '',
                    },
                },
                {
                    price_id: passData?.price?.[1]?.price_id || null,
                    seat_type_code: passData?.price?.[1]?.seat_type_code || 'VIP',
                    price: {
                        ADL: passData?.price?.[1]?.price?.ADL || '',
                        CHD: passData?.price?.[1]?.price?.CHD || '',
                        INF: passData?.price?.[1]?.price?.INF || '',
                        SNR: passData?.price?.[1]?.price?.SNR || '',
                    },
                },
            ],
        },
        validationSchema: Yup.object({
            name: Yup.object({
                en: Yup.string().required('Name in English is required'),
                ar: Yup.string().required('Name in Arabic is required'),
            }),
            description: Yup.object({
                en: Yup.string().required('Description in English is required'),
                ar: Yup.string().required('Description in Arabic is required'),
            }),
            valid_from: Yup.string().required('From date is required'),
            valid_to: Yup.string().required('To date is required'),
            price: Yup.array().of(
                Yup.object().shape({
                    price_id: Yup.string().nullable(),
                    seat_type_code: Yup.string().nullable(),
                    price: Yup.object({
                        ADL: Yup.number()
                            .typeError('Adult price must be a number')
                            .min(0, 'Adult price cannot be negative')
                            .required('Adult price is required'),
                        CHD: Yup.number()
                            .typeError('Child price must be a number')
                            .min(0, 'Child price cannot be negative')
                            .required('Child price is required'),
                        INF: Yup.number()
                            .typeError('Infant  price must be a number')
                            .min(0, 'Infant price cannot be negative')
                            .required('Infant price is required'),
                        SNR: Yup.number()
                            .typeError('Senior price must be a number')
                            .min(0, 'Senior price cannot be negative')
                            .required('Senior price is required'),
                    }),
                })
            ),
        }),
        onSubmit: values => {
            setSubmitLoading(true)
            CreateBoatDayPassFormValidation.setSubmitting(true)
            console.log(values)
            submitBoat(values)
        },
    })

    const submitBoat = async values => {
        try {
            const priceData = {
                ...values,
                price: values.price.map(p => ({
                    ...p,
                    price: {
                        ADL: parseFloat(p.price.ADL),
                        CHD: parseFloat(p.price.CHD),
                        INF: parseFloat(p.price.INF),
                        SNR: parseFloat(p.price.SNR),
                    },
                })),
            }
            if (id) {
                updateBoatDayPass({
                    variables: {
                        dayPassPrice: priceData,
                        masterPriceId: id,
                    },
                })
            } else {
                addNewBoatDayPass({
                    variables: {
                        dayPassPrice: priceData,
                    },
                })
            }
            console.log('priceData===', priceData)
        } catch (error) {
            CreateBoatDayPassFormValidation.setSubmitting(false)
        }
    }
    return (
        <>
            <div className="mb-8 flex justify-between items-center">
                <button
                    type="button"
                    onClick={handleBack}
                    className="inline-flex items-center gap-x-0.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Back')}
                </button>
                <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={handleBack}
                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                        {t('Cancel')}
                    </button>
                    <button
                        disabled={CreateBoatDayPassFormValidation.isSubmitting}
                        onClick={CreateBoatDayPassFormValidation.handleSubmit}
                        type="button"
                        className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    >
                        {t('Save')}
                    </button>
                </div>
            </div>
            <form onSubmit={CreateBoatDayPassFormValidation.handleSubmit}>
                <div className="space-y-6">
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-5 md:grid-cols-3">
                        <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">
                                {t('Day Pass Information')}
                            </h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">
                                {t('Enter the name and description of the day pass')}
                            </p>
                        </div>

                        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="name.en"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Name (English)')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="name.en"
                                        name="name.en"
                                        type="text"
                                        placeholder="Enter name in English"
                                        value={CreateBoatDayPassFormValidation.values.name.en}
                                        onChange={CreateBoatDayPassFormValidation.handleChange}
                                        onBlur={CreateBoatDayPassFormValidation.handleBlur}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateBoatDayPassFormValidation?.errors?.name?.en &&
                                        CreateBoatDayPassFormValidation?.touched?.name?.en ? (
                                        <div className="text-red text-sm">
                                            {CreateBoatDayPassFormValidation?.errors?.name?.en}
                                        </div>
                                    ) : null}
                                </div>
                            </div>

                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="name.ar"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Name (Arabic)')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="name.ar"
                                        name="name.ar"
                                        type="text"
                                        placeholder="أدخل الاسم بالعربية"
                                        value={CreateBoatDayPassFormValidation?.values?.name?.ar}
                                        onChange={CreateBoatDayPassFormValidation?.handleChange}
                                        onBlur={CreateBoatDayPassFormValidation?.handleBlur}
                                        dir="rtl"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateBoatDayPassFormValidation?.errors?.name?.ar &&
                                        CreateBoatDayPassFormValidation?.touched?.name?.ar ? (
                                        <div className="text-red text-sm">
                                            {CreateBoatDayPassFormValidation?.errors?.name?.ar}
                                        </div>
                                    ) : null}
                                </div>
                            </div>

                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="description.en"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Description (English)')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <textarea
                                        id="description.en"
                                        name="description.en"
                                        rows={3}
                                        placeholder="Enter description in English"
                                        value={CreateBoatDayPassFormValidation.values.description.en}
                                        onChange={CreateBoatDayPassFormValidation.handleChange}
                                        onBlur={CreateBoatDayPassFormValidation.handleBlur}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateBoatDayPassFormValidation?.errors?.description?.en &&
                                        CreateBoatDayPassFormValidation.touched.description?.en ? (
                                        <div className="text-red text-sm">
                                            {CreateBoatDayPassFormValidation?.errors?.description?.en}
                                        </div>
                                    ) : null}
                                </div>
                            </div>

                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="description.ar"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Description (Arabic)')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <textarea
                                        id="description.ar"
                                        name="description.ar"
                                        rows={3}
                                        placeholder="أدخل الوصف بالعربية"
                                        value={CreateBoatDayPassFormValidation?.values?.description?.ar}
                                        onChange={CreateBoatDayPassFormValidation?.handleChange}
                                        onBlur={CreateBoatDayPassFormValidation?.handleBlur}
                                        dir="rtl"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateBoatDayPassFormValidation?.errors?.description?.ar &&
                                        CreateBoatDayPassFormValidation?.touched?.description?.ar ? (
                                        <div className="text-red text-sm">
                                            {CreateBoatDayPassFormValidation?.errors?.description?.ar}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-5 md:grid-cols-3">
                        <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">
                                {t('Day Pass Validity')}
                            </h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">
                                {t('Enter the validity date of the day pass')}
                            </p>
                        </div>

                        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="valid_from"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Valid from')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <Flatpickr
                                        value={CreateBoatDayPassFormValidation.values.valid_from}
                                        onChange={date =>
                                            CreateBoatDayPassFormValidation.setFieldValue(
                                                'valid_from',
                                                date[0] ? date[0].toLocaleDateString('en-CA').replace(/\//g, '-') : ''
                                            )
                                        }
                                        options={{
                                            altInput: true,
                                            altFormat: 'Y/m/d',
                                            dateFormat: 'Y/m/d',
                                            value: CreateBoatDayPassFormValidation.values.valid_from,
                                            defaultDate: CreateBoatDayPassFormValidation.values.valid_from,
                                            maxDate: CreateBoatDayPassFormValidation.values.valid_to,
                                        }}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateBoatDayPassFormValidation.errors.valid_from &&
                                        CreateBoatDayPassFormValidation.touched.valid_from ? (
                                        <div className="text-red text-sm">
                                            {CreateBoatDayPassFormValidation.errors.valid_from}
                                        </div>
                                    ) : null}
                                </div>
                            </div>

                            <div className="sm:col-span-3">
                                <label htmlFor="valid_to" className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Valid to')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <Flatpickr
                                        value={CreateBoatDayPassFormValidation.values.valid_to}
                                        onChange={date =>
                                            CreateBoatDayPassFormValidation.setFieldValue(
                                                'valid_to',
                                                date[0] ? date[0].toLocaleDateString('en-CA').replace(/\//g, '-') : ''
                                            )
                                        }
                                        options={{
                                            altInput: true,
                                            altFormat: 'Y/m/d',
                                            dateFormat: 'Y/m/d',
                                            value: CreateBoatDayPassFormValidation.values.valid_to,
                                            defaultDate: CreateBoatDayPassFormValidation.values.valid_to,
                                            minDate: CreateBoatDayPassFormValidation.values.valid_from,
                                        }}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateBoatDayPassFormValidation.errors.valid_to &&
                                        CreateBoatDayPassFormValidation.touched.valid_to ? (
                                        <div className="text-red text-sm">
                                            {CreateBoatDayPassFormValidation.errors.valid_to}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 gap-x-8 gap-y-4 border-b border-gray-900/10 pb-5 md:grid-cols-3">
                        <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">
                                {t('Price Information')}
                            </h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">
                                {t('Enter the detailed price of the day pass for all categories')}
                            </p>
                        </div>

                        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                            {CreateBoatDayPassFormValidation.values.price.map((price, index) => (
                                <div key={index + 1} className="sm:col-span-6">
                                    <div className="grid max-w-2xl grid-cols-1 gap-x-3 sm:grid-cols-6 md:col-span-2">
                                        <div className="sm:col-span-full mt-0 border-b border-gray-200">
                                            <div className="sm:col-span-full flex gap-1 items-center border-b p-3 px-0">
                                                <TicketIcon aria-hidden="true" className={`h-5 w-5 ${price?.seat_type_code === 'VIP' ? 'text-gold' : 'text-blue'
                                                    }`} />
                                                <div className="text-end flex justify-between w-full">
                                                    <p className="sm:col-span-full text-sm font-medium text-gray-900">
                                                        {price.seat_type_code === 'VIP' ? t('VIP') : t('Standard')}
                                                    </p>
                                                    <p className="sm:col-span-full text-sm font-medium text-gray-400">
                                                        {t('Price in BHD')}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="sm:col-span-full grid grid-cols-1 sm:grid-cols-12 gap-3 sm:col-span-full px-3 py-6 bg-gray-50">
                                                <div className="sm:col-span-3">
                                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                                        {t('Adult')}
                                                        <span className="text-red">*</span>
                                                    </label>
                                                    <input
                                                        id={`price.${index}.price.ADL`}
                                                        name={`price.${index}.price.ADL`}
                                                        type="number"
                                                        placeholder="Enter here.."
                                                        value={
                                                            CreateBoatDayPassFormValidation.values.price[index].price
                                                                .ADL
                                                        }
                                                        onChange={CreateBoatDayPassFormValidation.handleChange}
                                                        onBlur={CreateBoatDayPassFormValidation.handleBlur}
                                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                    />
                                                    {CreateBoatDayPassFormValidation.errors.price?.[index]?.price
                                                        ?.ADL &&
                                                        CreateBoatDayPassFormValidation.touched.price?.[index]?.price
                                                            ?.ADL ? (
                                                        <div className="text-red text-sm">
                                                            {
                                                                CreateBoatDayPassFormValidation.errors.price[index]
                                                                    .price.ADL
                                                            }
                                                        </div>
                                                    ) : null}
                                                </div>

                                                <div className="sm:col-span-3">
                                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                                        {t('Child')}
                                                        <span className="text-red">*</span>
                                                    </label>
                                                    <input
                                                        id={`price.${index}.price.CHD`}
                                                        name={`price.${index}.price.CHD`}
                                                        type="number"
                                                        value={
                                                            CreateBoatDayPassFormValidation.values.price[index].price
                                                                .CHD
                                                        }
                                                        onChange={CreateBoatDayPassFormValidation.handleChange}
                                                        onBlur={CreateBoatDayPassFormValidation.handleBlur}
                                                        placeholder="Enter here.."
                                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                    />
                                                    {CreateBoatDayPassFormValidation.errors.price?.[index]?.price
                                                        ?.CHD &&
                                                        CreateBoatDayPassFormValidation.touched.price?.[index]?.price
                                                            ?.CHD ? (
                                                        <div className="text-red text-sm">
                                                            {
                                                                CreateBoatDayPassFormValidation.errors.price[index]
                                                                    .price.CHD
                                                            }
                                                        </div>
                                                    ) : null}
                                                </div>

                                                <div className="sm:col-span-3">
                                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                                        {t('Infant')}
                                                        <span className="text-red">*</span>
                                                    </label>
                                                    <input
                                                        id={`price.${index}.price.INF`}
                                                        name={`price.${index}.price.INF`}
                                                        type="number"
                                                        value={
                                                            CreateBoatDayPassFormValidation.values.price[index].price
                                                                .INF
                                                        }
                                                        onChange={CreateBoatDayPassFormValidation.handleChange}
                                                        placeholder="Enter here.."
                                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                    />
                                                    {CreateBoatDayPassFormValidation.errors.price?.[index]?.price
                                                        ?.INF &&
                                                        CreateBoatDayPassFormValidation.touched.price?.[index]?.price
                                                            ?.INF ? (
                                                        <div className="text-red text-sm">
                                                            {
                                                                CreateBoatDayPassFormValidation.errors.price[index]
                                                                    .price.INF
                                                            }
                                                        </div>
                                                    ) : null}
                                                </div>

                                                <div className="sm:col-span-3">
                                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                                        {t('Senior Citizen')}
                                                        <span className="text-red">*</span>
                                                    </label>
                                                    <input
                                                        id={`price.${index}.price.SNR`}
                                                        name={`price.${index}.price.SNR`}
                                                        type="number"
                                                        value={
                                                            CreateBoatDayPassFormValidation.values.price[index].price
                                                                .SNR
                                                        }
                                                        onChange={CreateBoatDayPassFormValidation.handleChange}
                                                        onBlur={CreateBoatDayPassFormValidation.handleBlur}
                                                        placeholder="Enter here.."
                                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                    />
                                                    {CreateBoatDayPassFormValidation.errors.price?.[index]?.price
                                                        ?.SNR &&
                                                        CreateBoatDayPassFormValidation.touched.price?.[index]?.price
                                                            ?.SNR ? (
                                                        <div className="text-red text-sm">
                                                            {
                                                                CreateBoatDayPassFormValidation.errors.price[index]
                                                                    .price.SNR
                                                            }
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="mt-6 flex items-center justify-end gap-x-3">
                    <button
                        onClick={handleBack}
                        type="button"
                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                        {t('Cancel')}
                    </button>
                    <button
                        type="submit"
                        className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    >
                        {t('Save')}
                    </button>
                </div>
            </form>
            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={dialogError} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog
                    status={true}
                    title={t('Success')}
                    message={t('Saved Successfully')}
                    onClose={handleDialogClose}
                />
            )}
            {/* Overlay and Loading Spinner */}
            {submitLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                </div>
            )}
        </>
    )
}
